<template>
  <div class="page">
    <div class="rules">
      <div class="card">
        <div class="card-header">
          <h3 class="mb-0 text-center">{{ ipProfile.name }} Rules</h3>
        </div>
        <div class="card-body">
          <loading
            loader="dots"
            :active.sync="loading"
            :is-full-page="false"
            color="#2196f3"
          ></loading>
          <div class="mb-4">
            <p>
              IP Profile:
              <span class="font-weight-bold">{{ ipProfile.name }}</span>
            </p>
            <div class="d-flex justify-content-between align-items-center">
              <div class="searchbar">
                <TextInput
                  class="my-0"
                  placeholder="Enter search query..."
                  v-model="query"
                />
              </div>
              <div v-if="currentUser.role === 'SuperAdmin'">
                <b-button
                  :variant="reOrderMode ? 'outline-warning' : 'outline-info'"
                  outline
                  v-if="
                    filteredBlocks.length &&
                    !filteredBlocks[filteredBlocks.length - 1].new &&
                    filteredBlocks.length > 1
                  "
                  @click.prevent="reOrderMode = !reOrderMode"
                >
                  <span v-if="reOrderMode">Exit Re-order mode</span>
                  <span v-else>Re-order mode</span>
                </b-button>
              </div>
            </div>
          </div>
          <!-- rules -->
          <draggable
            v-model="blocks"
            handle=".handleBlocks"
            :disabled="filteredBlocks.length <= 1"
          >
            <transition-group>
              <RuleBox
                v-for="(block, idx) in filteredBlocks"
                :idx="idx"
                :parent_id="ipProfile.id"
                :key="block.id || idx"
                v-bind="block"
                :conditionNames="conditionNames"
                :variable_names="variable_names"
                :reOrderMode="reOrderMode"
                @delete="onBlockDelete(block, idx)"
                @duplicate="onDuplicate(idx)"
                @refreshBlocks="getRules($route.params.id)"
                base="/sbc/ips"
              />
            </transition-group>
          </draggable>
          <div class="d-flex justify-content-end">
            <b-button
              variant="primary"
              @click.prevent="create"
              v-if="currentUser.role === 'SuperAdmin'"
            >
              Create new block
            </b-button>
            <b-button
              variant="primary"
              v-if="reOrderMode"
              @click.prevent="reOrder"
            >
              Confirm Order
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from "@/components/TextInput.vue";
import RuleBox from "@/components/RuleBox.vue";
import http from "@/axios";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { parseFunction } from "@/utils";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      query: "",
      loading: false,
      ipProfile: {},
      conditionNames: [],
      variable_names: [],
      blocks: [],
      reOrderMode: false,
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "IP Profiles", route: "ip-profiles/all" },
      { title: "Rules" },
    ]);
  },
  created() {
    this.getConditionNames();
    this.getVariableNames();
    this.getRules(this.$route.params.id);
    this.getIpProfile(this.$route.params.id);
  },
  methods: {
    // Utils
    onDuplicate(idx) {
      const currentBlock = {
        ...this.blocks[idx],
        visible: true,
        editable: true,
        new: true,
        title: `${this.blocks[idx].title} (duplicated)`,
      };
      this.blocks = [...this.blocks, currentBlock];
    },
    create() {
      this.blocks.push({
        id: `${new Date().getTime()}`,
        title: "",
        match: [
          {
            condition_name: "",
            related_variables: [],
            operator: "",
            value: "",
            id: new Date().getTime(),
          },
        ],
        deploy: [
          {
            variable_name: "",
            variable_name_value: {
              name: "",
              params: [],
            },
            related_variables: [],
            varNameType: "text",
            id: new Date().getTime(),
          },
        ],
        editable: true,
        new: true,
        visible: true,
      });
    },

    // API calls
    async getIpProfile(id) {
      this.loading = true;
      try {
        const res = await http.get(`sbc/ips/show/${id}`);
        this.loading = false;

        this.ipProfile = res.data.data;
        this.$store.dispatch(SET_BREADCRUMB, [
          { title: "IP Profiles", route: "ip-profiles/all" },
          { title: `${res.data.data.name} Rules` },
        ]);
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    async getRules(id) {
      this.loading = true;
      try {
        const res = await http.get(`sbc/ips/rules/${id}`);
        this.loading = false;
        let rules = res.data.data;

        rules = rules.map((rule) => {
          const deploy = rule.deploy.map((deploy) => ({
            ...deploy,
            ...parseFunction(deploy.variable_name_value),
            id: new Date().getTime(),
          }));
          const match = rule.match.map((match) => ({
            ...match,
            id: new Date().getTime(),
          }));
          return {
            ...rule,
            match,
            deploy,
            editable: false,
            new: false,
            visible: false,
          };
        });

        this.blocks = rules;
      } catch (error) {
        this.loading = false;
      }
    },
    async getConditionNames() {
      this.loading = true;
      try {
        const res = await http.get("/sbc/variables/match");
        this.conditionNames = res.data.data.map(({ name, key }) => ({
          text: name,
          value: key,
        }));
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$notify({
          group: "settings",
          title: error.response.data.message,
          type: "error",
        });
      }
    },
    async getVariableNames() {
      this.loading = true;

      try {
        const res = await http.get("/sbc/variables/deploy");
        this.variable_names = res.data.data.map(({ name, key }) => ({
          text: name,
          value: key,
        }));
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$notify({
          group: "settings",
          title: error.response.data.message,
          type: "error",
        });
      }
    },
    async onBlockDelete(block, idx) {
      const { isConfirmed } = await Swal.fire({
        title: "Are you sure to delete this rule?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#2196f3",
        cancelButtonColor: "#F64E60",
        confirmButtonText: "Yes, delete it!",
      });

      if (isConfirmed) {
        try {
          this.loading = true;
          if (!block.new) await http.delete(`/sbc/ips/rules/${block.id}`);
          this.blocks.splice(idx, 1);
          this.loading = false;
          Swal.fire(
            "Deleted!",
            "the rule has been deleted successfully.",
            "success"
          );
        } catch (error) {
          this.loading = false;
          this.$notify({
            group: "settings",
            title: error.response.data.message,
            type: "error",
          });
        }
      }
    },
    async reOrder() {
      this.loading = true;
      const blockIds = this.blocks.map(({ id }) => id);

      try {
        const res = await http.put(`sbc/ips/rules/re-order`, { ids: blockIds });
        this.$notify({
          group: "edit-rule",
          title: res.data.message,
          type: "success",
        });
        this.loading = false;
        this.reOrderMode = false;
      } catch (error) {
        this.$notify({
          group: "edit-rule",
          title: "Something went wrong!",
          type: "error",
        });
        this.loading = false;
      }
    },
  },
  computed: {
    ...mapGetters(["currentUser"]),
    filteredBlocks() {
      const blocks = this.blocks.filter((block) =>
        block.title.toLowerCase().includes(this.query.toLowerCase())
      );
      return blocks;
    },
  },
  components: { TextInput, RuleBox },
};
</script>
